import React, {useContext} from 'react'
import AppContext from '../../appContext'
import LandResponse from '../../controllers/LandResponse'
import SimpleCoordinate from '../../controllers/SimpleCoordinate'
import CheckBox from '../../components/CheckBox'

interface LimeBlocksTabProps {
    sampleCounts: Map<number, number>
    unmatchedCount: number
    selectedLandIds: number[]
    onSelectedChange: (selectedLandIds: number[]) => void
    farmLands: LandResponse[]
    boundaryMap: Record<number, SimpleCoordinate[]>
    showUnmatched: boolean
    onShowUnmatchedChange: (value: boolean) => void
}

function calculateAreaHectares(coordinates: Array<{x: number; y: number}>): number {
    if (coordinates.length < 3) return 0
    const toRad = (deg: number) => deg * Math.PI / 180
    let area = 0
    const n = coordinates.length
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n
        const coord1 = coordinates[i]
        const coord2 = coordinates[j]
        if (!coord1 || !coord2) continue
        const lat1 = coord1.x
        const lon1 = coord1.y
        const lat2 = coord2.x
        const lon2 = coord2.y
        area += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)))
    }
    const earthRadius = 6371000
    area = Math.abs(area) * earthRadius * earthRadius / 2
    return area / 10000
}

const LimeBlocksTab: React.FC<LimeBlocksTabProps> = ({sampleCounts, unmatchedCount, selectedLandIds, onSelectedChange, farmLands, boundaryMap, showUnmatched, onShowUnmatchedChange}) => {
    const context = useContext(AppContext)

    function toggleBlock(landId: number) {
        if (selectedLandIds.includes(landId)) {
            onSelectedChange(selectedLandIds.filter(id => id !== landId))
        } else {
            onSelectedChange([...selectedLandIds, landId])
        }
    }

    return (
        <div className="p-4">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b text-left text-gray-600">
                        <th className="p-2 w-10"></th>
                        <th className="p-2">{context.word('block_name')}</th>
                        <th className="p-2 text-right">{context.word('area')} (ha)</th>
                        <th className="p-2 text-right">{context.word('samples')}</th>
                    </tr>
                </thead>
                <tbody>
                    {unmatchedCount > 0 && (
                        <tr
                            className={`border-b cursor-pointer hover:bg-gray-50 ${showUnmatched ? '' : 'opacity-50'}`}
                            onClick={() => onShowUnmatchedChange(!showUnmatched)}
                        >
                            <td className="p-2">
                                <CheckBox value={showUnmatched} onChange={() => onShowUnmatchedChange(!showUnmatched)}/>
                            </td>
                            <td className="p-2 italic text-gray-500">{context.word('unmatched')}</td>
                            <td className="p-2"></td>
                            <td className="p-2 text-right">{unmatchedCount}</td>
                        </tr>
                    )}
                    {farmLands.map(land => {
                        const coords = boundaryMap[land.id]
                        const area = coords ? calculateAreaHectares(coords) : null
                        const isSelected = selectedLandIds.includes(land.id)
                        const sampleCount = sampleCounts.get(land.id) ?? 0
                        return (
                            <tr
                                key={land.id}
                                className={`border-b cursor-pointer hover:bg-gray-50 ${isSelected ? '' : 'opacity-50'}`}
                                onClick={() => toggleBlock(land.id)}
                            >
                                <td className="p-2">
                                    <CheckBox value={isSelected} onChange={() => toggleBlock(land.id)}/>
                                </td>
                                <td className="p-2 font-medium">{land.name}</td>
                                <td className="p-2 text-right">{area !== null ? area.toFixed(2) : '—'}</td>
                                <td className="p-2 text-right">{sampleCount}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default LimeBlocksTab
